import { useMemo, ReactNode } from "react";
import type { ColumnDef } from "../../components/base-table-layout";
import { createEhrWriteBackColumn } from "../../ehr/create-ehr-column";
import { useEhrContext } from "../../../shared-logic/ehr-context/ehr-context";
import { BaseTableLayout } from "../../components/base-table-layout";
import { TableRow } from "../../utils/tables-metadata";
import { LabRowData } from "./lab-rows";

export type IndividualLabTableProps = {
  rows: LabRowData[];
  onRowClick: (row: TableRow) => void;
  filterQuery: string;
  displayName?: string;
  isRowViewed?: (row: TableRow) => boolean;
  headerActionsSlot?: ReactNode;
};

export function IndividualLabTable({
  rows,
  onRowClick,
  filterQuery,
  displayName = "Labs",
  isRowViewed,
  headerActionsSlot,
}: IndividualLabTableProps) {
  const ehrContext = useEhrContext();

  const columns = useMemo(() => {
    const cols: ColumnDef<LabRowData>[] = [
      { key: "observation", header: "Observation", maxWidth: "300px" },
      { key: "value", header: "Value" },
      { key: "interpretation", header: "Interpretation", hideOnSmallScreen: true },
      { key: "referenceRange", header: "Reference Range", hideOnSmallScreen: true },
      { key: "date", header: "Date", width: "120px", sort: "desc" },
    ];

    const ehrColumn = createEhrWriteBackColumn<LabRowData>(ehrContext, "lab");
    if (ehrColumn) cols.push(ehrColumn);

    return cols;
  }, [ehrContext]);

  function handleRowClick(row: TableRow) {
    // Clear focusedObservationId to disable highlighting in individual view
    const rowWithoutFocus = { ...row, focusedObservationId: undefined };
    onRowClick(rowWithoutFocus);
  }

  function getRowStyle(row: LabRowData) {
    return {
      ...(row.backgroundColor ? { bg: row.backgroundColor } : {}),
      ...(row.hoverColor ? { _hover: { bg: row.hoverColor } } : {}),
    };
  }

  return (
    <BaseTableLayout
      title={displayName}
      rows={rows as { id: string }[]}
      columns={columns as ColumnDef<{ id: string }>[]}
      filterQuery={filterQuery}
      onRowClick={handleRowClick}
      isRowViewed={isRowViewed}
      headerActionsSlot={headerActionsSlot}
      getRowStyle={row => getRowStyle(row as unknown as LabRowData)}
    />
  );
}
